//T.32832 - Servicio para tracking de camiones con geolocalización
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, interval, of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { ICamionTracking, IGarita, IAlertaGeofence } from '../model/tracking-camion.model';
import { ITrackingViajes, ITicketResponse, IGeoreferenciaRequest, IVerificarEspacioResponse } from '../model/modelTracking';
import { UtilsService } from '../../../shared/utils/utils.service';
import { CommonService } from '../../../shared/utils/common.service';

@Injectable({
  providedIn: 'root'
})
export class TrackingCamionService {

  //T.32832 - Configuración de la garita (punto fijo)
  private readonly GARITA: IGarita = {
    nombre: 'Garita Principal',
    latitud: -0.995114,
    longitud: -80.688585,
    radioMetros: 500
  };

  //T.32832 - Almacena el estado anterior de geofence para detectar cambios
  private estadosAnteriores: Map<string, boolean> = new Map();

  //T.32832 - Base URL para el API
  private urlLogistica: string;

  constructor(
    private http: HttpClient, 
    private utilServices: UtilsService,
    private commonService: CommonService
  ) { 
    this.urlLogistica = this.utilServices.getUrlLogistica();
  }

  //T.32832 - Obtiene la configuración de la garita
  getGarita(): IGarita {
    return this.GARITA;
  }
  
  /*
  //T.32832 - Simula obtener camiones activos (usuarios con rol CAMIONES)
  //En producción, esto vendría de un API real
  getCamionesActivos(): Observable<ICamionTracking[]> {
    return interval(15000).pipe( // Actualiza cada 15 segundos
      switchMap(() => this.generarDatosCamiones())
    );
  }


  //T.32832 - Obtiene camiones una sola vez (para carga inicial)
  getCamionesInicial(): Observable<ICamionTracking[]> {
    return this.generarDatosCamiones();
  }

  //T.32832 - Genera datos simulados de camiones
  //En producción, reemplazar con llamada al API real
  private generarDatosCamiones(): Observable<ICamionTracking[]> {
    const camiones: ICamionTracking[] = [
      {
        id: '1',
        placa: 'GYE-001',
        chofer: 'Juan Pérez',
        cedula: '0912345678',
        latitud: -0.995114 + (Math.random() - 0.5) * 0.02, // Cerca de la garita
        longitud: -80.688585 + (Math.random() - 0.5) * 0.02,
        velocidad: Math.floor(Math.random() * 60),
        timestamp: new Date(),
        enGeofence: false,
        distanciaGarita: 0,
        estado: 'En ruta'
      },
      {
        id: '2',
        placa: 'GYE-002',
        chofer: 'María González',
        cedula: '0923456789',
        latitud: -0.995114 + (Math.random() - 0.5) * 0.01, // Más cerca de la garita
        longitud: -80.688585 + (Math.random() - 0.5) * 0.01,
        velocidad: Math.floor(Math.random() * 60),
        timestamp: new Date(),
        enGeofence: false,
        distanciaGarita: 0,
        estado: 'En ruta'
      },
      {
        id: '3',
        placa: 'GYE-003',
        chofer: 'Carlos Rodríguez',
        cedula: '0934567890',
        latitud: -0.995114 + (Math.random() - 0.5) * 0.03, // Lejos de la garita
        longitud: -80.688585 + (Math.random() - 0.5) * 0.03,
        velocidad: Math.floor(Math.random() * 60),
        timestamp: new Date(),
        enGeofence: false,
        distanciaGarita: 0,
        estado: 'En ruta'
      }
    ];

    //T.32832 - Calcula distancia y verifica geofence para cada camión
    camiones.forEach(camion => {
      camion.distanciaGarita = this.calcularDistancia(
        camion.latitud,
        camion.longitud,
        this.GARITA.latitud,
        this.GARITA.longitud
      );
      camion.enGeofence = camion.distanciaGarita <= this.GARITA.radioMetros;
    });

    return of(camiones);
  }
*/
  //T.32832 - Calcula distancia entre dos puntos usando fórmula de Haversine
  calcularDistancia(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371000; // Radio de la Tierra en metros
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);
    
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distancia = R * c;
    
    return Math.round(distancia); // Retorna distancia en metros
  }

  //T.32832 - Convierte grados a radianes
  private toRad(grados: number): number {
    return grados * (Math.PI / 180);
  }

  //T.32832 - Verifica si un camión está dentro del geofence
  estaEnGeofence(camion: ICamionTracking): boolean {
    return camion.distanciaGarita <= this.GARITA.radioMetros;
  }

  //T.32832 - Detecta si un camión acaba de entrar al geofence
  detectarEntradaGeofence(camion: ICamionTracking): boolean {
    const estadoAnterior = this.estadosAnteriores.get(camion.id);
    const estadoActual = camion.enGeofence;

    //T.32832 - Actualiza el estado
    this.estadosAnteriores.set(camion.id, estadoActual);

    //T.32832 - Retorna true si cambió de fuera a dentro
    return estadoAnterior === false && estadoActual === true;
  }

  //T.32832 - Inicializa el estado de un camión
  inicializarEstado(camion: ICamionTracking): void {
    this.estadosAnteriores.set(camion.id, camion.enGeofence);
  }

  //T.32832 - Obtiene viajes activos del chofer desde el endpoint
  getViajesActivosChofer(cedulaChofer: string): Observable<ITrackingViajes[]> {
    const url = `${this.urlLogistica}tracking-viaje/activos/chofer/${cedulaChofer}`;
    
    return this.commonService.peticionGetConHeader(url).pipe(
      catchError(error => {
        console.error('Error al obtener viajes activos:', error);
        // Retornar array vacío en caso de error
        return of([]);
      })
    );
  }

  //T.32832 - Genera ticket para el viaje cuando el camión entra a la garita
  generarTicketChofer(idViaje: number): Observable<ITicketResponse> {
    const url = `${this.urlLogistica}tracking-viaje/${idViaje}/chofer-ticket`;
    
    return this.commonService.peticionPostConHeader(url, {}).pipe(
      catchError(error => {
        console.error('Error al generar ticket:', error);
        throw error;
      })
    );
  }

  //T.32832 - Guarda la georeferencia del vehículo (crea o actualiza según el estado)
  guardarGeoreferencia(georeferencia: IGeoreferenciaRequest): Observable<any> {
    const url = `${this.urlLogistica}tracking-viaje-camion/georeferencia`;
    
    return this.commonService.peticionPostConHeader(url, georeferencia).pipe(
      catchError(error => {
        console.error('Error al guardar georeferencia:', error);
        // No lanzar error para no interrumpir el tracking
        return of(null);
      })
    );
  }

  //T.32832 - Verifica si hay espacio disponible para el camión
  verificarEspacio(idViaje: number): Observable<IVerificarEspacioResponse> {
    const url = `${this.urlLogistica}tracking-viaje/${idViaje}/verificar-espacio`;
    
    return this.commonService.peticionPutConHeader(url, {}).pipe(
      catchError(error => {
        console.error('Error al verificar espacio:', error);
        // No lanzar error para no interrumpir el tracking
        return of(null);
      })
    );
  }

  //T.32832 - Actualiza el estado de un viaje
  updateEstadoViaje(idViaje: number, estado: string, modificadoPor: string): Observable<any> {
    const url = `${this.urlLogistica}tracking-viaje/${idViaje}/estado?estado=${estado}&modificadoPor=${modificadoPor}`;
    
    return this.commonService.peticionPutConHeader(url, {}).pipe(
      catchError(error => {
        console.error('Error al actualizar estado del viaje:', error);
        throw error;
      })
    );
  }

  //T.32832 - Obtiene viajes por estado
  getViajesPorEstado(estado: string): Observable<ITrackingViajes[]> {
    const url = `${this.urlLogistica}tracking-viaje/estado/${estado}`;
    console.log('T.32832 - Llamando servicio getViajesPorEstado:', url);
    
    return this.commonService.peticionGetConHeader(url).pipe(
      map((response: any) => {
        console.log('T.32832 - Respuesta del servicio getViajesPorEstado:', response);
        
        // Verificar si la respuesta es un array directamente
        if (Array.isArray(response)) {
          console.log('T.32832 - Respuesta es un array directo. Viajes encontrados:', response.length);
          return response;
        }
        
        // Si viene en response.respuesta
        if (response && response.respuesta) {
          console.log('T.32832 - Viajes encontrados en response.respuesta:', response.respuesta.length);
          return response.respuesta;
        }
        
        console.log('T.32832 - No se encontraron viajes en la respuesta');
        return [];
      }),
      catchError(error => {
        console.error('T.32832 - Error al obtener viajes por estado:', error);
        return of([]);
      })
    );
  }

  //T.32832 - Obtiene todos los viajes con información de área
  getViajesConArea(): Observable<ITrackingViajes[]> {
    const url = `${this.urlLogistica}tracking-viaje/viajes-con-area`;
    console.log('T.32832 - Llamando servicio getViajesConArea:', url);
    
    return this.commonService.peticionGetConHeader(url).pipe(
      map((response: any) => {
        console.log('T.32832 - Respuesta del servicio getViajesConArea:', response);
        
        // Verificar si la respuesta es un array directamente
        if (Array.isArray(response)) {
          console.log('T.32832 - Respuesta es un array directo. Viajes encontrados:', response.length);
          return response;
        }
        
        // Si viene en response.respuesta
        if (response && response.respuesta) {
          console.log('T.32832 - Viajes encontrados en response.respuesta:', response.respuesta.length);
          return response.respuesta;
        }
        
        console.log('T.32832 - No se encontraron viajes en la respuesta');
        return [];
      }),
      catchError(error => {
        console.error('T.32832 - Error al obtener viajes con área:', error);
        return of([]);
      })
    );
  }

  //T.32832 - Actualiza el orden de la cola de viajes
  actualizarCola(viajes: ITrackingViajes[]): Observable<any> {
    const url = `${this.urlLogistica}tracking-viaje/actualizar-cola`;
    console.log('T.32832 - Llamando servicio actualizarCola:', url);
    console.log('T.32832 - Datos enviados:', viajes);
    
    return this.commonService.peticionPutConHeader(url, viajes).pipe(
      map((response: any) => {
        console.log('T.32832 - Respuesta del servicio actualizarCola:', response);
        return response;
      }),
      catchError(error => {
        console.error('T.32832 - Error al actualizar cola:', error);
        return of({ key: 500, status: 'ERROR', mensaje: 'Error al actualizar cola' });
      })
    );
  }

  //T.32832 - Habilita un viaje (cambia estado a REVISION_HABILITADA)
  //Modificado para usar el endpoint PUT /{idViaje}/estado con query params
  habilitarViaje(idViaje: number): Observable<any> {
    // Obtener el usuario actual del localStorage
    let modificadoPor = 'SISTEMA';
    try {
      const datosUsuario = JSON.parse(localStorage.getItem('datosUsuarioLogin') || '{}');
      modificadoPor = datosUsuario.login || 'SISTEMA';
    } catch (error) {
      console.warn('T.32832 - Error al obtener usuario del localStorage:', error);
    }

    // Construir URL con query params según el endpoint especificado
    const url = `${this.urlLogistica}tracking-viaje/${idViaje}/estado?estado=REVISION_HABILITADA&modificadoPor=${modificadoPor}`;
    console.log('T.32832 - Llamando servicio updateEstado:', url);
    
    return this.commonService.peticionPutConHeader(url, {}).pipe(
      map((response: any) => {
        console.log('T.32832 - Respuesta del servicio updateEstado:', response);
        return response;
      }),
      catchError(error => {
        console.error('T.32832 - Error al actualizar estado del viaje:', error);
        return of({ key: 500, status: 'ERROR', mensaje: 'Error al actualizar estado del viaje' });
      })
    );
  }
}
